import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Actions as ApiCallActions } from '../../../redux/apiCall/reducers'
import general from '../../../utils/general'
import FileUpload from '../../../components/file-upload'
import useI18n from '../../../i18n/useI18n'

const ProgramFeesBulkUpdate = ({ universityId, onBack, onSuccess }) => {
    const { t } = useI18n()
    const dispatch = useDispatch()

    const [downloading, setDownloading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [uploadKey, setUploadKey] = useState(0)

    const handleDownload = () => {
        if (downloading) return

        setDownloading(true)
        dispatch(
            ApiCallActions.Get({
                url: `universities/${universityId}/program-fees/excel`,
                axiosOptions: {
                    responseType: 'blob'
                },
                showAlertOnError: true,
                onSuccess: ({ data }) => {
                    const blobSource = data?.data || data
                    if (!blobSource) {
                        general.notificationError(t('COMMON_ERROR'))
                        return
                    }

                    const blob = blobSource instanceof Blob
                        ? blobSource
                        : new Blob([blobSource], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        })

                    const downloadUrl = window.URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = downloadUrl
                    link.download = `program-fees-${universityId}.xlsx`
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    window.URL.revokeObjectURL(downloadUrl)

                    general.messageSuccess(t('PROGRAM_FEES_BULK_UPDATE_SUCCESS_DOWNLOAD', 'Dosya indirildi'))
                },
                onError: ({ errorMessage }) => {
                    general.notificationError(errorMessage || t('COMMON_ERROR'))
                },
                callback: () => {
                    setDownloading(false)
                }
            })
        )
    }

    const handleFileSelect = (file) => {
        setSelectedFile(file)
    }

    const handleUpload = () => {
        if (!selectedFile) {
            general.notificationWarning(t('PROGRAMS_BULK_UPLOAD_NO_FILE'))
            return
        }

        setUploading(true)
        const formData = new FormData()
        formData.append('file', selectedFile)

        dispatch(
            ApiCallActions.Post({
                url: `universities/${universityId}/program-fees/excel/upload`,
                axiosOptions: {
                    data: formData
                },
                showAlertOnError: true,
                onSuccess: () => {
                    general.messageSuccess(t('PROGRAM_FEES_BULK_UPDATE_SUCCESS'))
                    setSelectedFile(null)
                    setUploadKey(prev => prev + 1)
                    if (onSuccess) onSuccess()
                },
                onError: ({ errorMessage }) => {
                    general.notificationError(errorMessage || t('PROGRAM_FEES_BULK_UPDATE_ERROR'))
                },
                callback: () => {
                    setUploading(false)
                }
            })
        )
    }

    return (
        <div className="card">
            <div className="header">
                <h2>
                    {t('PROGRAM_FEES_BULK_UPDATE_TITLE')}
                    <small>{t('PROGRAM_FEES_BULK_UPDATE_DESCRIPTION')}</small>
                </h2>
            </div>
            <div className="body">
                <div className="row clearfix">
                    <div className="col-lg-7 col-md-12">
                        <div className="alert" style={{ color: '#4a4a4aff' }}>
                            <h6 className="m-b-10"><i className="zmdi zmdi-info m-r-5"></i>{t('SIDEBAR_INFO_HEADER')}</h6>
                            <ol className="m-b-0 p-l-20" style={{ textAlign: 'left' }}>
                                <li className="m-b-5">{t('PROGRAM_FEES_BULK_UPDATE_STEP_DOWNLOAD')}</li>
                                <li className="m-b-5">{t('PROGRAM_FEES_BULK_UPDATE_STEP_EDIT')}</li>
                                <li>{t('PROGRAM_FEES_BULK_UPDATE_STEP_UPLOAD')}</li>
                            </ol>
                        </div>

                        <div className="alert alert-warning">
                            <i className="zmdi zmdi-alert-triangle m-r-5"></i>
                            {t('PROGRAM_FEES_BULK_UPDATE_WARNING')}
                        </div>

                        <button
                            type="button"
                            className="btn btn-raised btn-round btn-primary m-r-10"
                            onClick={handleDownload}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                            disabled={downloading}
                        >
                            {downloading ? (
                                <>
                                    <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                                    {t('PROGRAM_FEES_BULK_UPDATE_DOWNLOADING')}
                                </>
                            ) : (
                                <>
                                    <i className="zmdi zmdi-download m-r-5"></i>
                                    {t('PROGRAM_FEES_BULK_UPDATE_BUTTON_DOWNLOAD')}
                                </>
                            )}
                        </button>
                    </div>

                    <div className="col-lg-5 col-md-12">
                        <div className="card m-b-0">
                            <div className="body p-0">
                                <FileUpload
                                    key={uploadKey}
                                    accept=".xlsx"
                                    label={t('PROGRAM_FEES_BULK_UPDATE_FILE_LABEL')}
                                    showPreview={false}
                                    onFileSelect={handleFileSelect}
                                    maxSize={10}
                                />

                                <button
                                    type="button"
                                    className="btn btn-primary btn-block btn-lg m-t-20"
                                    onClick={handleUpload}
                                    disabled={uploading || !selectedFile}
                                >
                                    {uploading ? (
                                        <>
                                            <i className="zmdi zmdi-refresh zmdi-hc-spin m-r-5"></i>
                                            {t('PROGRAM_FEES_BULK_UPDATE_UPLOADING')}
                                        </>
                                    ) : (
                                        <>
                                            <i className="zmdi zmdi-check m-r-5"></i>
                                            {t('PROGRAM_FEES_BULK_UPDATE_BUTTON_UPLOAD')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProgramFeesBulkUpdate
